import { Outlet, useOutletContext, useParams } from 'react-router-dom'
import { Group, Skeleton, Stack } from '@mantine/core'
import { StationContext } from '@/pages/stations/station-context'
import { GetStationHeaderGql } from '@/graphql/queries/get-station-header.graphql'
import { useAuthenticatedGraphQuery } from '@/hooks/use-authenticated-query'
import { StationHeader } from '@/components/stations/header/station-header'
import { PageContainer } from '@shared/components/app-shell/page-container/page-container'
import { addTitleSuffix } from '@/constants/page-titles'
import { AlertLevel, IconAlert } from '@shared/components/icon-alert/icon-alert'
import { OutletContextProps } from '@shared/components/app-shell/app-shell'

export const StationLayout = () => {
  const { stationRecordNumber } = useParams()
  const outletContext: OutletContextProps = useOutletContext()

  const { data, isLoading, isError } = useAuthenticatedGraphQuery({
    queryKey: ['stationData', stationRecordNumber],
    gql: GetStationHeaderGql,
    queryParams: { recordNumber: stationRecordNumber },
    alwaysThrowOnError: true,
    retry: false,
  })

  return (
    <StationContext.Provider value={data?.stationHeader ?? null}>
      <PageContainer title={addTitleSuffix(data?.stationHeader?.brand || '')}>
        {isError && (
          <IconAlert
            level={AlertLevel.ERROR}
            title='Error'
            message='Failed to fetch station data'
          />
        )}
        {data && !data?.stationHeader && (
          <IconAlert
            level={AlertLevel.ERROR}
            title='Station not found'
            message={`No station found with record number ${stationRecordNumber}`}
          />
        )}
        <Stack>
          <Group justify='space-between' align-items='center' preventGrowOverflow>
            {isLoading ? (
              <>
                <Skeleton visible height={45} width='60%' />
                <Skeleton visible height={45} width='30%' />
              </>
            ) : (
              <StationHeader
                brand={data?.stationHeader?.brand || ''}
                recordNumber={data?.stationHeader?.recordNumber}
                onHold={data?.stationHeader?.isOnHold || false}
                isLoading={isLoading || !data}
                stationsInGroup={data?.stationHeader?.stationsInGroup || []}
              />
            )}
          </Group>
          {isLoading ? <Skeleton visible height={400} /> : <Outlet context={outletContext} />}
        </Stack>
      </PageContainer>
    </StationContext.Provider>
  )
}
